import React, { useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import './ReviewPage.css';

const ReviewPage = () => {
  const { state } = useLocation();
  const { patientUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { appointmentData, doctorFee, totalFee, selectedDoctor, platformFeeRate } = state || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('razorpay');

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    if (!patientUser || !patientUser.accessToken) {
      setError('No valid patient token available. Please log in again.');
      setLoading(false);
      navigate('/login?type=patient');
      return;
    }

    if (!window.Razorpay) {
      setError('Razorpay script not loaded. Please refresh the page or check your internet connection.');
      setLoading(false);
      return;
    }

    try {
      console.log('Starting payment process with amount:', totalFee);
      const paymentResponse = await api.post('/appointments/create-order', {
        appointmentId: null,
        amount: totalFee,
      }, { headers: { Authorization: `Bearer ${patientUser.accessToken}` } });

      console.log('Order created:', paymentResponse.data);
      const { orderId, amount, currency, key } = paymentResponse.data;

      const options = {
        key,
        amount,
        currency,
        name: 'TureMedicine',
        description: 'Appointment Payment',
        order_id: orderId,
        handler: async (response) => {
          console.log('Razorpay payment response:', response);
          try {
            const verifyResponse = await api.post('/appointments/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              appointmentId: null,
            }, { headers: { Authorization: `Bearer ${patientUser.accessToken}` } });

            console.log('Verify payment response:', verifyResponse.data);
            if (!verifyResponse.data.success) {
              throw new Error(verifyResponse.data.error || 'Payment verification failed');
            }

            const bookingData = {
              ...appointmentData,
              patientId: patientUser.id,
              doctorId: appointmentData.doctorId,
              consultationFee: doctorFee,
              totalFee,
              transactionId: response.razorpay_payment_id,
            };
            console.log('Sending booking data:', bookingData);

            const bookingResponse = await api.post('/appointments/book', bookingData, {
              headers: { Authorization: `Bearer ${patientUser.accessToken}` },
            });
            console.log('Booking response (raw):', bookingResponse);
            console.log('Booking response data:', bookingResponse.data);

            let appointmentId = bookingResponse.data.appointment?._id || bookingResponse.data.appointment?.id;
            if (!appointmentId) {
              console.error('No valid appointmentId found in response:', bookingResponse.data);
              throw new Error('Booking succeeded but no appointment ID returned');
            }

            console.log('Payment and booking completed successfully. Navigating to /payment-success with:', {
              appointmentId,
              transactionId: response.razorpay_payment_id,
              amount: totalFee,
              appointmentData,
              doctorName: selectedDoctor.name,
            });
            setError('');
            navigate('/payment-success', {
              state: {
                appointmentId,
                transactionId: response.razorpay_payment_id,
                amount: totalFee,
                appointmentData,
                doctorName: selectedDoctor.name,
              },
            });
          } catch (error) {
            console.error('Error during payment/book process:', {
              message: error.message,
              response: error.response ? error.response.data : null,
              status: error.response ? error.response.status : null,
              stack: error.stack,
            });
            setError(`Payment processing failed: ${error.message}`);
            navigate('/payment-failed', {
              state: {
                errorMessage: error.message,
                appointmentData,
                doctorFee,
                totalFee,
                selectedDoctor,
              },
            });
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: appointmentData.name,
          email: patientUser.email || '',
          contact: patientUser.phone || '',
        },
        theme: { color: '#3399cc' },
        modal: {
          ondismiss: () => {
            console.log('Payment modal dismissed by user');
            setError('Payment was cancelled by the user.');
            setLoading(false);
            navigate('/payment-failed', {
              state: { errorMessage: 'Payment was cancelled by the user', appointmentData, doctorFee, totalFee, selectedDoctor },
            });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        console.error('Razorpay payment failed:', response.error);
        setError(`Payment failed: ${response.error.description}`);
        setLoading(false);
        navigate('/payment-failed', {
          state: { errorMessage: response.error.description, appointmentData, doctorFee, totalFee, selectedDoctor },
        });
      });
      rzp.open();
    } catch (err) {
      console.error('Error initiating payment:', { message: err.message, response: err.response?.data });
      setError(`Failed to initiate payment: ${err.message}`);
      setLoading(false);
      navigate('/payment-failed', {
        state: { errorMessage: err.message, appointmentData, doctorFee, totalFee, selectedDoctor },
      });
    }
  };

  if (!appointmentData || !selectedDoctor) {
    return (
      <div className="review-page">
        <h2>Error</h2>
        <p>Invalid appointment data.</p>
        <button onClick={() => navigate("/login?type=patient")}>Go to Login</button>
      </div>
    );
  }

  if (!patientUser || patientUser.role !== 'patient') {
    return (
      <div className="review-page">
        <h2>Error</h2>
        <p>You must be logged in as a patient to book an appointment.</p>
        <button onClick={() => navigate("/login?type=patient")}>Go to Patient Login</button>
        <button onClick={() => navigate("/doctor-login")}>Go to Doctor Login</button>
      </div>
    );
  }

  return (
    <div className="review-page">
      <h2>Review Your Appointment</h2>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      <div className="review-details">
        <p><strong>Patient Name:</strong> {appointmentData.name}</p>
        <p><strong>Doctor Name:</strong> {selectedDoctor.name}</p>
        <p><strong>Date:</strong> {new Date(appointmentData.date).toLocaleString()}</p>
        <p><strong>Symptoms:</strong> {appointmentData.symptoms || 'N/A'}</p>
        <p><strong>Consultation Fee:</strong> ₹{totalFee.toFixed(2)}</p>
      </div>
      <div className="payment-methods">
        <h3>Choose Payment Method</h3>
        <label>
          <input
            type="radio"
            name="paymentMethod"
            value="razorpay"
            checked={paymentMethod === 'razorpay'}
            onChange={() => setPaymentMethod('razorpay')}
          />
          Razorpay (UPI, Cards, Net Banking, Wallets)
        </label>
      </div>
      <button onClick={handlePayment} disabled={loading} className="payment-button">
        {loading ? (
          <span>
            <span className="spinner"></span> Processing...
          </span>
        ) : (
          'Proceed to Payment'
        )}
      </button>
      <button onClick={() => navigate(-1)}>Back</button>
    </div>
  );
};

export default ReviewPage;